const TEXT_ESCAPES: Record<string, string> = { '&': '&amp;', '<': '&lt;', '>': '&gt;' };

/** A text node's characters as HTML text — `textContent` is decoded, so it must be re-escaped before reuse as HTML. */
const escapeHtmlText = (text: string): string => text.replace(/[&<>]/g, (char) => TEXT_ESCAPES[char] ?? char);

/**
 * Splits resolved prose HTML into top-level segments for pagination.
 * Each element child (and contiguous non-empty text) becomes one "row" for paginate().
 * Every segment stays HTML: elements keep their serialized markup and loose text is re-escaped, so a
 * substituted value escaped upstream is never decoded back into markup here.
 */
export function splitProseBodyHtml(html: string): string[] {
  const trimmed = html.trim();
  if (!trimmed) return [];

  if (typeof document === 'undefined') {
    return splitProseBodyHtmlFallback(trimmed);
  }

  const template = document.createElement('template');
  template.innerHTML = trimmed;
  const segments: string[] = [];
  let textBuffer = '';

  const flushText = () => {
    const text = textBuffer.trim();
    if (text) segments.push(escapeHtmlText(text));
    textBuffer = '';
  };

  for (const node of Array.from(template.content.childNodes)) {
    if (node.nodeType === Node.TEXT_NODE) {
      textBuffer += node.textContent ?? '';
      continue;
    }
    if (node.nodeType === Node.ELEMENT_NODE) {
      flushText();
      segments.push((node as Element).outerHTML);
    }
  }
  flushText();

  if (segments.length === 0) return [trimmed];
  return segments;
}

/** Regex fallback when DOM is unavailable (non-browser tooling). */
function splitProseBodyHtmlFallback(html: string): string[] {
  const parts = html.match(/<([a-z][a-z0-9]*)\b[^>]*>[\s\S]*?<\/\1>|[^<]+|<[^>]+\/?>/gi);
  if (!parts?.length) return [html];

  const segments: string[] = [];
  let textBuffer = '';
  for (const part of parts) {
    const trimmedPart = part.trim();
    if (!trimmedPart) continue;
    if (trimmedPart.startsWith('<')) {
      if (textBuffer) {
        segments.push(textBuffer);
        textBuffer = '';
      }
      segments.push(trimmedPart);
      continue;
    }
    textBuffer += (textBuffer ? ' ' : '') + trimmedPart;
  }
  if (textBuffer) segments.push(textBuffer);
  return segments.length > 0 ? segments : [html];
}
